import {
  Button,
  FormLabel,
  HStack,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  TrackedButton,
  type TrackedButtonProps,
} from '$/components/common/Button/TrackedButton';
import { TrackedSection } from '$/components/common/TrackedSection';
import { useTrackInteraction } from '$/hooks/useTrackInteraction';
import { trackContentImpression } from '$/logger';
import { GlobalFavoritePublishModalContent } from '$/pages/DashboardPages/pages/Favorite/components/GlobalFavoritePublishModalContent';
import { useFavoriteModal } from '$/pages/DashboardPages/pages/Favorite/hooks/useFavoriteModal';
import {
  changeFolderOrder,
  setGlobalFavoriteFolderState,
} from '$/services/usecases/favorites';
import { DEFAULTFOLDERNAME, useFavoriteStore } from '$/stores/useFavoriteStore';
import { useMaterialContextMenuStore } from '$/stores/useMaterialContextMenuStore';
import { type PiwikContentPiece } from '$/utils/piwikUtils';

type ModalInfo = {
  header: string;
  body?: ReactNode;
  footer?: ReactNode;
};

export const FavoriteModal = () => {
  const { t } = useTranslation();

  const {
    isOpen,
    onClose,
    modalMode,
    itemIdentifier,
    itemType,
    itemName,
    menuLayer,
  } = useFavoriteModal();
  const deleteFolder = useFavoriteStore.useRemoveFolder();
  const removeFavorite = useFavoriteStore.useRemoveFavorite();
  const renameFolder = useFavoriteStore.useRenameFolder();
  const addFavoriteToFolder = useFavoriteStore.useAddFavoriteToFolder();
  const addFolder = useFavoriteStore.useAddFolder();
  const folders = useFavoriteStore.useFavoriteFolders();
  const favorites = useFavoriteStore.useFavorites();
  const activeFolderId = useFavoriteStore.useActiveFolderId();
  const getFavorites = useFavoriteStore.useGetFavorites();
  const setActiveMaterialContextMenuId =
    useMaterialContextMenuStore.useSetActiveMaterial();
  const getMemorizedCheckedItems =
    useMaterialContextMenuStore.useGetMemorizedCheckedItems();
  const memorizeCheckedItems =
    useMaterialContextMenuStore.useMemorizeCheckedItems();

  const [nameInputValue, setNameInputValue] = useState('');
  const [fileData, setFileData] = useState<File | null>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const onChangeGlobalFavoriteFolder = async (
    folderId: string,
    isGlobal: boolean,
  ) => {
    await setGlobalFavoriteFolderState(folderId, isGlobal, fileData);
    await getFavorites(true);
  };

  const getCurrentContentPiece = useCallback(():
    | PiwikContentPiece
    | undefined => {
    switch (modalMode) {
      case 'deleteFavorite':
        return 'ManageFavorite';
      case 'createFolder':
        return 'NewFolder';
    }
  }, [modalMode]);

  useEffect(() => {
    if (isOpen) {
      const contentPiece = getCurrentContentPiece();

      if (!contentPiece) {
        return;
      }

      trackContentImpression('Favorite', contentPiece, 'Open');
    }
  }, [isOpen, modalMode, getCurrentContentPiece]);

  const trackCloseButton = useTrackInteraction(
    'Favorite',
    getCurrentContentPiece(),
    'Close',
  );

  const onRenameSection = async () => {
    const activeFolder = folders.find((folder) => folder.id === activeFolderId);
    if (activeFolder == null || activeFolder.sections == null) return;

    const newSections = activeFolder.sections.map((section) => {
      if (section.id === itemIdentifier) {
        return { ...section, title: nameInputValue };
      }
      return section;
    });
    await changeFolderOrder(
      activeFolder.id,
      activeFolder.materialOrder,
      newSections,
    );
    await useFavoriteStore.getState().getFavorites(true);
  };

  const onDeleteSection = async () => {
    const activeFolder = folders.find((folder) => folder.id === activeFolderId);
    if (activeFolder == null || activeFolder.sections == null) return;

    const sectionToDeleteIndex = activeFolder.sections?.findIndex(
      (section) => section.id === itemIdentifier,
    );

    if (sectionToDeleteIndex === -1) return;

    if (sectionToDeleteIndex === 0) {
      const newSections = activeFolder.sections;
      newSections[0].title = undefined;
      await changeFolderOrder(
        activeFolder.id,
        activeFolder.materialOrder,
        newSections,
      );
      await getFavorites(true);
      return;
    }

    const materialsInSectionToDelete =
      activeFolder.sections[sectionToDeleteIndex].favoriteIds;

    const newSections =
      activeFolder.sections?.filter(
        (section) => !(section.id === itemIdentifier),
      ) ?? [];

    newSections[sectionToDeleteIndex - 1].favoriteIds = [
      ...newSections[sectionToDeleteIndex - 1].favoriteIds,
      ...materialsInSectionToDelete,
    ];

    await changeFolderOrder(
      activeFolder.id,
      activeFolder.materialOrder,
      newSections,
    );
    await getFavorites(true);
  };

  const buildFooter = (
    primaryButtonText: string,
    primaryAction: () => void | Promise<void>,
    options?: {
      primaryButtonCustomProps?: TrackedButtonProps;
      customSecondaryButton?: ReactNode;
    },
  ) => (
    <>
      {options?.customSecondaryButton ?? (
        <Button
          px='0'
          fontSize='sm'
          onClick={() => {
            setNameInputValue('');
            onClose();
            setIsSubmitDisabled(false);
          }}
          variant='text'
        >
          {t('favorites.cancelButton')}
        </Button>
      )}
      <TrackedButton
        fontSize='sm'
        {...options?.primaryButtonCustomProps}
        isDisabled={
          isSubmitDisabled || options?.primaryButtonCustomProps?.isDisabled
        }
        onClick={async () => {
          await primaryAction();
          onClose();
        }}
      >
        {primaryButtonText}
      </TrackedButton>
    </>
  );

  useEffect(() => {
    setNameInputValue(itemName);
    setFileData(null);
  }, [itemIdentifier, itemName]);

  const { body, header, footer }: ModalInfo = (() => {
    switch (modalMode) {
      case 'deleteFavorite': {
        const favorite = favorites.find(
          (favorite) => favorite.materialId === itemIdentifier,
        );

        return {
          header: t('favorites.removeFavoriteHeader'),
          body: (
            <>
              <Trans
                i18nKey='favorites.removeFavoriteInformation'
                values={{ materialName: itemName }}
                shouldUnescape
              />
              <UnorderedList mt='4' fontSize='sm'>
                {favorite?.parentFolderIds.includes(DEFAULTFOLDERNAME) && (
                  <ListItem>{t('favorites.generalStorage')}</ListItem>
                )}
                {folders
                  .filter((folder) =>
                    favorite?.parentFolderIds.includes(folder.id),
                  )
                  .map((folder) => (
                    <ListItem key={folder.id}>{folder.name}</ListItem>
                  ))}
              </UnorderedList>
            </>
          ),
          footer: buildFooter(
            t('favorites.removeFavoriteHeader'),
            () => removeFavorite(itemIdentifier),
            {
              primaryButtonCustomProps: {
                variant: 'danger',
                contentName: 'Favorite',
                contentPiece: 'ManageFavorite',
                contentTarget: 'Delete',
              },
              customSecondaryButton: (
                <TrackedButton
                  p='0'
                  fontSize='sm'
                  onClick={() => {
                    trackContentImpression('Favorite', 'EditFolder', 'Open');
                    setActiveMaterialContextMenuId(
                      itemIdentifier,
                      itemType,
                      menuLayer,
                    );
                    onClose();
                  }}
                  variant='text'
                  contentName='Favorite'
                  contentPiece='ManageFavorite'
                  contentTarget='EditFolder'
                >
                  {t('favorites.changeFolder')}
                </TrackedButton>
              ),
            },
          ),
        };
      }
      case 'renameFolder':
        return {
          header: t('favorites.renameButton'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('favorites.insertNameLabel')}
              </FormLabel>
              <Input
                onChange={(event) => setNameInputValue(event.target.value)}
                value={nameInputValue}
              />
            </>
          ),
          footer: buildFooter(
            t('favorites.saveButton'),
            () => renameFolder(itemIdentifier, nameInputValue),
            {
              primaryButtonCustomProps: {
                isDisabled:
                  nameInputValue.length === 0 || itemName === nameInputValue,
              },
            },
          ),
        };
      case 'createFolder':
        return {
          header: t('favorites.newFolder'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('favorites.insertNameLabel')}
              </FormLabel>
              <TrackedSection
                contentInteraction='ClickButton'
                contentName='Favorite'
                contentPiece='NewFolder'
                contentTarget='InsertName'
              >
                <Input
                  onChange={(event) => setNameInputValue(event.target.value)}
                  value={nameInputValue}
                />
              </TrackedSection>
            </>
          ),
          footer: buildFooter(
            t('favorites.saveButton'),
            async () => {
              const newFolderId = await addFolder(nameInputValue);
              if (itemIdentifier != null && newFolderId != null) {
                await addFavoriteToFolder(itemIdentifier, newFolderId);
                setActiveMaterialContextMenuId(
                  itemIdentifier,
                  itemType,
                  menuLayer,
                );
                const memorizedCheckedItems = getMemorizedCheckedItems();

                if (memorizedCheckedItems) {
                  memorizeCheckedItems([...memorizedCheckedItems, newFolderId]);
                }
              }
            },
            {
              primaryButtonCustomProps: {
                isDisabled: nameInputValue.length === 0,
                contentName: 'Favorite',
                contentPiece: 'NewFolder',
                contentTarget: 'Submit',
              },
              customSecondaryButton: (
                <TrackedButton
                  px='0'
                  fontSize='sm'
                  onClick={() => {
                    setNameInputValue('');
                    onClose();
                  }}
                  variant='text'
                  contentName='Favorite'
                  contentPiece='NewFolder'
                  contentTarget='Cancel'
                >
                  {t('favorites.cancelButton')}
                </TrackedButton>
              ),
            },
          ),
        };
      case 'deleteFolder':
        return {
          header: t('favorites.deleteFolderHeader'),
          body: <Text>{t('favorites.deleteFolderInformation')}</Text>,
          footer: buildFooter(
            t('favorites.deleteButton'),
            () => deleteFolder(itemIdentifier),
            { primaryButtonCustomProps: { variant: 'danger' } },
          ),
        };
      case 'publishFolder':
        return {
          header: t('favorites.publishModal.header'),
          body: (
            <GlobalFavoritePublishModalContent
              itemIdentifier={itemIdentifier}
              file={fileData}
              onFileSet={setFileData}
              setSubmitDisabled={setIsSubmitDisabled}
            />
          ),
          footer: buildFooter(t('favorites.publishModal.publishButton'), () =>
            onChangeGlobalFavoriteFolder(itemIdentifier, true),
          ),
        };
      case 'hideFolder':
        return {
          header: t('favorites.hideModal.header'),
          body: (
            <Text whiteSpace='pre-line'>
              <Trans
                i18nKey='favorites.hideModal.description'
                values={{
                  folderName: folders.find(
                    (folder) => folder.id === itemIdentifier,
                  )?.name,
                }}
                shouldUnescape
              />
            </Text>
          ),
          footer: buildFooter(
            t('favorites.hideModal.hideButton'),
            () => onChangeGlobalFavoriteFolder(itemIdentifier, false),
            { primaryButtonCustomProps: { variant: 'danger' } },
          ),
        };
      case 'deleteSection':
        return {
          header: t('favorites.deleteSectionModalTitle'),
          body: (
            <Text whiteSpace='pre-line'>
              <Trans
                i18nKey='favorites.deleteSectionDescription'
                values={{ sectionName: itemName }}
                shouldUnescape
              />
            </Text>
          ),
          footer: buildFooter(t('favorites.deleteSection'), onDeleteSection, {
            primaryButtonCustomProps: { variant: 'danger' },
          }),
        };
      case 'renameSection':
        return {
          header: t('favorites.renameSection'),
          body: (
            <>
              <FormLabel fontSize='sm'>
                {t('favorites.insertNameLabel')}
              </FormLabel>
              <Input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onChange={(event) => setNameInputValue(event.target.value)}
                value={nameInputValue}
              />
            </>
          ),
          footer: buildFooter(t('favorites.renameSection'), onRenameSection, {
            primaryButtonCustomProps: {
              isDisabled:
                nameInputValue.trim().length === 0 ||
                nameInputValue.length > 100 ||
                itemName === nameInputValue,
            },
          }),
        };
    }
  })();

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={() => {
        setNameInputValue('');
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent px='10' py='10' bg='background' borderRadius='xl'>
        <ModalCloseButton top='8' right='8' onClick={trackCloseButton} />
        <ModalHeader mb='4' p='0'>
          {header}
        </ModalHeader>
        <ModalBody mb='10' p='0' fontSize='sm'>
          {body}
        </ModalBody>
        <ModalFooter p='0'>
          <HStack justifyContent='space-between' w='full'>
            {footer}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
